import { Table } from '@components/table';
import { ItemResponse } from '@models/order/items/response';
import {
    orderItemsSelector,
    resetSellerState,
} from '@redux/slices/order';
import { Col, Row, Table as AntTable, Tag } from 'antd';
import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    itemsColumns,
    orderColumn,
    itemsColumnsReadOnly,
    orderColumnReadOnly,
    bonificationItemsColumns,
    marketingBalanceMaxColumn,
} from '../constants';
import { SelectItemsTableFooter } from './select-items-table-footer';
import '@extensions/string';
import '@extensions/array';
import { OrderRequest } from '@models/order/items/request';
import { useQuery } from '@hooks/use-query';
import {
    selectedCampaignsSelector,
} from '@redux/slices/campaign';
import { colors } from '@utils/constants';
import { translate } from '@components/i18n';
import { AppDispatch } from '@redux/store';
import { balanceSelector } from '@redux/slices/balance';

interface SelectItemsBodyProps {
    bonification?: boolean;
    marketing?: boolean;
}

export const SelectItemsBody: React.FC<SelectItemsBodyProps> = memo(
    ({ bonification = false, marketing = false }) => {
        const dispatch = useDispatch<AppDispatch>();
        const selectItemsState = useSelector(orderItemsSelector);
        const campaigns = useSelector(selectedCampaignsSelector);
        const { useBalance } = useSelector(balanceSelector)
        const query = useQuery();

        const isDraft = query.get('draft') === 'true';

        useEffect(() => {
            if (isDraft) {
                dispatch(resetSellerState());
            }
        }, []);

        const filteredItems = (i: ItemResponse): boolean => {
            const value = i[selectItemsState.filter?.filterBy];
            if (
                !value &&
                (i.description.compare(selectItemsState.filter?.filterValue) ||
                    `${i.code}`.compare(selectItemsState.filter?.filterValue) ||
                    `${i.codigoTipoTransporte}`.compare(
                        `${selectItemsState.filter?.filterValue}`,
                    ))
            )
                return true;
            else if (
                value &&
                `${value}`.compare(selectItemsState.filter?.filterValue)
            )
                return true;
            return false;
        };

        return (
            <Col>
                <Table
                    columns={
                        bonification
                            ? useBalance
                                ? [...bonificationItemsColumns, marketingBalanceMaxColumn]
                                : bonificationItemsColumns
                            : selectItemsState.order?.order
                                ? itemsColumns.insert(8, orderColumn)
                                : itemsColumns
                    }
                    rowKey={(record: ItemResponse) => `${record.id}`}
                    dataSource={selectItemsState.availableItems.filter(
                        filteredItems,
                    )}
                    summary={
                        campaigns.length > 0
                            ? (pageData) => (
                                <AntTable.Summary fixed={true}>
                                    <AntTable.Summary.Row>
                                        <AntTable.Summary.Cell
                                            index={2}
                                            colSpan={16}
                                        >
                                            <Row wrap={false}>
                                                <b
                                                    style={{
                                                        marginRight: 5,
                                                        opacity: 0.5,
                                                    }}
                                                >
                                                    {translate(
                                                        'general.activeCampains',
                                                    )}
                                                    :
                                                </b>
                                                {campaigns
                                                    .reverse()
                                                    .map((c, i) => (
                                                        <Tag
                                                            color={colors[i]}
                                                        >
                                                            {c.nome}
                                                        </Tag>
                                                    ))}
                                            </Row>
                                        </AntTable.Summary.Cell>
                                    </AntTable.Summary.Row>
                                </AntTable.Summary>
                            )
                            : undefined
                    }
                    isLoading={selectItemsState.isFetching}
                    pagination={undefined}
                    footerObject={
                        <SelectItemsTableFooter marketing={marketing} bonification={bonification} />
                    }
                    scroll={{ y: '40vh' }}
                />
            </Col>
        );
    },
);

interface SelectItemsBodyBonificationProps {
    order: Partial<OrderRequest>;
}

export const SelectItemsBodyBonification: React.FC<SelectItemsBodyBonificationProps> =
    memo(({ order }) => {
        const selectItemsState = useSelector(orderItemsSelector);
        const filteredItems = (i: ItemResponse): boolean => {
            const value = i[selectItemsState.filter?.filterBy];
            if (
                !value &&
                (i.description.compare(selectItemsState.filter?.filterValue) ||
                    `${i.code}`.compare(selectItemsState.filter?.filterValue) ||
                    `${i.codigoTipoTransporte}`.compare(
                        `${selectItemsState.filter?.filterValue}`,
                    ))
            )
                return true;
            else if (
                value &&
                `${value}`.compare(selectItemsState.filter?.filterValue)
            )
                return true;
            return false;
        };

        return (
            <Col>
                <Table
                    columns={
                        order?.order
                            ? itemsColumnsReadOnly.insert(
                                8,
                                orderColumnReadOnly,
                            )
                            : itemsColumnsReadOnly
                    }
                    rowKey={(record: ItemResponse) => `${record.id}`}
                    dataSource={order.availableItems
                        .filter(filteredItems)
                        .sort((a, b) =>
                            a.description.localeCompare(b.description),
                        )}
                    pagination={undefined}
                    footerObject={<SelectItemsTableFooter order={order} bonification />}
                    scroll={{ y: '40vh' }}
                />
            </Col>
        );
    });
